import { useState, useEffect, useCallback } from 'react';
import { useFilterStore } from '../stores/filter-store';
// import { marketplaceOrdersApi } from '../lib/api';
import { graphqlApi, mapStatusToGraphQL } from '../lib/graphql';
import { useAuthStore } from '../stores/auth-store';

interface UseFulfillmentOptions {
  storeId?: number;
  autoFetch?: boolean;
}

export function useFulfillment(options: UseFulfillmentOptions = {}) {
  const { storeId, autoFetch = true } = options;
  
  const { token } = useAuthStore();
  const { 
    fulfillmentFilters, 
    getFulfillmentApiFilters, 
    setLoading 
  } = useFilterStore();
  
  const [orders, setOrders] = useState<any[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchOrders = useCallback(async () => {
    if (!token || !storeId) {
      setError('Authentication or store ID required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setLoading(true);

    try {
      const filters = getFulfillmentApiFilters();
      const graphQLStatus = filters.type ? mapStatusToGraphQL(filters.type) : undefined;

      // Format dates to match GraphQL DateTime format (Y-m-d H:i:s)
      const formatDateForGraphQL = (dateString: string | undefined) => {
        if (!dateString) return undefined;
        return `${dateString} 00:00:00`;
      };

      console.log('🔍 useFulfillment - API filters received:', filters);
      console.log('🔍 useFulfillment - Raw status:', filters.type);
      console.log('🔍 useFulfillment - Mapped GraphQL status:', graphQLStatus);
      console.log('🔍 useFulfillment - Making GraphQL API call with sorting:', { sort: sortField, order: sortOrder });
      const response = await graphqlApi.getOrderListing(token, storeId, {
        status: graphQLStatus,
        search: filters.search ?? '',
        marketPlaceType: filters.marketplace_filter && filters.marketplace_filter.length > 0 ? filters.marketplace_filter.join(',') : '',
        orderDateFrom: formatDateForGraphQL(filters.date_from),
        orderDateTo: formatDateForGraphQL(filters.date_to),
        page: filters.page,
        first: filters.limit,
        sort: sortField,
        order: sortOrder,
      });

      if (response.errors) {
        throw new Error(response.errors?.[0]?.message || 'GraphQL query failed');
      }

      const payload = response.data?.orderListing;
      const list = payload?.data ?? [];
      const total = payload?.paginatorInfo?.total ?? 0;
      setOrders(list);
      setTotalOrders(total);
    } catch (err) {
      console.error('Fulfillment API error:', err); // Debug log
      setError(err instanceof Error ? err.message : 'An error occurred');
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [token, storeId, fulfillmentFilters, getFulfillmentApiFilters, setLoading, sortField, sortOrder]);

  // Auto-fetch when filters change
  useEffect(() => {
    if (autoFetch && token && storeId) {
      fetchOrders();
    }
  }, [fetchOrders, autoFetch, token, storeId]);

  const refreshOrders = useCallback(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateSorting = useCallback((field: string, order: 'asc' | 'desc') => {
    console.log('🔍 useFulfillment - updateSorting called with:', field, order);
    console.log('🔍 useFulfillment - Previous sortField:', sortField, 'sortOrder:', sortOrder);
    setSortField(field);
    setSortOrder(order);
    console.log('🔍 useFulfillment - Updated sortField:', field, 'sortOrder:', order);
  }, [sortField, sortOrder]);

  return {
    orders,
    totalOrders,
    error,
    isLoading,
    fetchOrders,
    refreshOrders,
    updateSorting,
    sortField,
    sortOrder,
    filters: fulfillmentFilters
  };
}
